import {
    ProductAttributeValueComponent as SourceProductAttributeValueComponent,
} from 'SourceComponent/ProductAttributeValue/ProductAttributeValue.component';

import './ProductAttributeValue.override.style';
import { AttributeType, STRING_ONLY_ATTRIBUTE_CODES, SelectAttributeType } from '@scandipwa/scandipwa/src/component/ProductAttributeValue/ProductAttributeValue.config';
import { getBooleanLabel } from '@scandipwa/scandipwa/src/util/Product';
import Html from '@scandipwa/scandipwa/src/component/Html';
import Field from 'Component/Field';
import React from "react";
import { noopFn } from '@scandipwa/scandipwa/src/util/Common';
import { FieldType } from '@scandipwa/scandipwa/src/component/Field/Field.config';

export class ProductAttributeValueComponent extends SourceProductAttributeValueComponent {
    static propTypes = {
        device: this.propTypes
    }

    static defaultProps = {
        isSelected: false,
        onClick: noopFn,
        getLink: noopFn,
        mix: {},
        isAvailable: true,
        isFormattedAsText: false,
        isProductCountVisible: false,
        showProductAttributeAsLink: true,
    };

    __construct(props) {
        super.__construct?.(props);

        this.clickHandler = this.clickHandler.bind(this);
        this.renderSublabel = this.renderSublabel.bind(this);
        this.getOptionLabel = this.getOptionLabel.bind(this);
        this.getCheckboxLabel = this.getCheckboxLabel.bind(this);
    }

    getIsColorLight(hex) {
        const color = (hex.charAt(0) === '#') ? hex.substring(1, 7) : hex;
        const r = parseInt(color.substring(0, 2), 16); // hexToR
        const g = parseInt(color.substring(2, 4), 16); // hexToG
        const b = parseInt(color.substring(4, 6), 16); // hexToB

        return ((r * 0.299) + (g * 0.587) + (b * 0.114)) > 186;
    }

    getOptionLabel(value) {
        const {
            attribute: {
                attribute_options,
                is_boolean,
            },
            isProductCountVisible,
        } = this.props;

        if (attribute_options) {
            const optionValues = attribute_options[value];

            if (optionValues) {
                if (!isProductCountVisible) {
                    const { label } = optionValues;

                    return { ...optionValues, labelText: label };
                }

                const { label, count = 0 } = optionValues;

                const adjustedLabel = getBooleanLabel(label, is_boolean);

                return {
                    ...optionValues,
                    label: `${adjustedLabel} (${count})`,
                    labelText: adjustedLabel.trim(),
                    count,
                };
            }
        }

        return {};
    }

    clickHandler(e) {
        const { onClick, attribute, isAvailable } = this.props;

        e.preventDefault();
        e.stopPropagation();

        if (!isAvailable) {
            return;
        }

        onClick(attribute);
    }

    onMouseHandler = () => {
        const { onMouseEnter, attribute } = this.props;

        onMouseEnter(attribute);
    }

    onMouseLeveHandler = () => {
        const { onMouseLeave, attribute } = this.props;

        onMouseLeave();
    }

    renderTextAttribute() {
        const { attribute: { attribute_value } } = this.props;

        return this.renderStringValue(attribute_value);
    }

    renderBooleanAttribute() {
        const { attribute: { attribute_value = '' } } = this.props;

        return this.renderStringValue(+attribute_value ? 'Yes' : 'No');
    }

    renderMultiSelectAttribute() {
        const { attribute: { attribute_value = '' } } = this.props;

        const labelsArray = attribute_value.split(',').reduce((labels, value) => {
            const { label } = this.getOptionLabel(value);

            if (label) {
                labels.push(label);
            }

            return labels;
        }, []);

        return this.renderStringValue(labelsArray.length ? labelsArray.join(', ') : 'N/A');
    }

    renderSelectAttribute() {
        const {
            attribute: {
                attribute_value = '',
                attribute_code = '',
                has_swatch,
            },
        } = this.props;
        const attributeOption = this.getOptionLabel(attribute_value);
        const {
            label, labelText, count, swatch_data,
        } = attributeOption;

        // skip attributes without valid swatches
        if (!swatch_data && has_swatch) {
            return null;
        }

        if (!swatch_data || STRING_ONLY_ATTRIBUTE_CODES.includes(attribute_code)) {
            return this.renderStringValue(labelText || 'N/A', null, count);
        }

        const { value, type } = swatch_data;

        switch (type) {
            case SelectAttributeType.STRING:
                return this.renderStringValue(value, labelText, count);
            case SelectAttributeType.COLOR:
                return this.renderColorValue(value, label);
            case SelectAttributeType.IMAGE:
                return this.renderImageValue(value, label, count);
            default:
                return this.renderStringValue(labelText || 'N/A', labelText, count);
        }
    }

    renderImageAttribute() {
        const {
            attribute: {
                attribute_label,
                attribute_value,
            },
        } = this.props;

        if (!attribute_value || attribute_value === 'no_selection') {
            return this.renderPlaceholder();
        }

        return (
            <img
                className='ProductAttributeValue-MediaImage'
                src={`/media/catalog/product${attribute_value}`}
                alt={attribute_label}
            />
        );
    }

    renderTextAreaAttribute() {
        const {
            attribute: { attribute_value = '' },
        } = this.props;

        return (
            <div className='ProductAttributeValue-TextArea'>
                <Html content={attribute_value} />
            </div>
        );
    }

    renderPlaceholder() {
        return (
            <div className='ProductAttributeValue-Placeholder' />
        );
    }

    renderColorValue(color, label) {
        const { isFormattedAsText, isSelected } = this.props;
        const isLight = this.getIsColorLight(color);

        if (isFormattedAsText) {
            return label || 'N/A';
        }

        const style = {
            '--option-background-color': color,
            '--option-border-color': isLight ? '#dddddd' : color,
            '--option-check-mark-background': isLight ? '#000' : '#fff',
            // stylelint-disable-next-line value-keyword-case
            '--option-is-selected': isSelected ? 1 : 0,
        };

        return (
            <data
                className={(isSelected ? "ProductAttributeValue-Color_isSelected " : "") + 'ProductAttributeValue-Color'}
                value={label}
                title={label}
                style={style}
            />
        );
    }

    renderImageValue(img, label, count = 0) {
        const { isFormattedAsText, isSelected } = this.props;

        if (isFormattedAsText) {
            return label || 'N/A';
        }

        const style = {
            '--option-is-selected': isSelected ? 1 : 0,
        };

        const className = isSelected ? "ProductAttributeValue-Image-container_isselected" : "";

        return (
            <div className={`ProductAttributeValue-Image-container ${className}`}>
                <div className='image-content'>
                    <img
                        className='ProductAttributeValue-Image'
                        src={`/media/attribute/swatch${img}`}
                        alt={label}
                        title={label}
                    />
                </div>
                {/* <data
                    className='ProductAttributeValue-Image-Overlay'
                    value={label}
                    title={label}
                    style={style}
                /> */}
                <span
                    className={isSelected ? "ProductAttributeValue-String ProductAttributeValue-String_isSelected" : 'ProductAttributeValue-String'}
                    title={label}
                >
                    {label}
                    <span>{` (${count})`}</span>
                </span>
            </div>
        );
    }

    renderSublabel(subLabel) {
        const { isProductCountVisible } = this.props;

        if (!subLabel || !isProductCountVisible) {
            return null;
        }

        return (
            <strong className='ProductAttributeValue-SubLabel'>
                {`(${subLabel})`}
            </strong>
        );
    }

    renderCheckboxValue(value) {
        if (typeof value !== 'string') {
            return value;
        }

        return <Html content={value} />;
    }

    getCheckboxLabel(value, subLabel) {
        return (
            <div className='ProductAttributeValue-Label'>
                {this.renderCheckboxValue(value)}
                {this.renderSublabel(subLabel)}
            </div>
        );
    }

    renderDropdown(value, subLabel) {
        const { isSelected } = this.props;

        return (
            <Field
                type={FieldType.CHECKBOX}
                attr={{
                    id: value,
                    name: value,
                    checked: isSelected,
                }}
                label={this.getCheckboxLabel(value, subLabel)}
                mix={{
                    block: 'ProductAttributeValue',
                    elem: 'Text',
                    mods: { isSelected },
                }}
            />
        );
    }

    renderValue(value) {
        if (value) {
            return <Html content={`${value}`} />;
        }

        return false;
    }

    renderStringValue(value, label, count = 0) {
        const { isFormattedAsText, isSelected } = this.props;
        const isSwatch = !!label;

        if (isFormattedAsText) {
            return label || this.renderValue(value) || 'N/A';
        }

        if (!isSwatch) {
            return this.renderDropdown(value || '', String(count));
        }

        const className = isSelected ? "ProductAttributeValue-String_isSelected" : "";

        return (
            <span
                className={`ProductAttributeValue-String-stringvalue ProductAttributeValue-String ${className}`}
                title={label}
            >
                {value}
                <span>{` (${count})`}</span>
            </span>
        );
    }

    renderNumericAttribute() {
        const { attribute: { attribute_value = '0' } } = this.props;

        return this.renderStringValue(parseFloat(attribute_value).toFixed(2));
    }

    renderAttributeByType() {
        const { attribute: { attribute_type, attribute_code } } = this.props;

        switch (attribute_type) {
            case AttributeType.SELECT:
                return this.renderSelectAttribute();
            case AttributeType.BOOLEAN:
                return this.renderBooleanAttribute();
            case AttributeType.TEXT:
                return this.renderTextAttribute();
            case AttributeType.MULTISELECT:
                return this.renderMultiSelectAttribute();
            case AttributeType.MEDIA_IMAGE:
                return this.renderImageAttribute();
            case AttributeType.TEXTAREA:
                return this.renderTextAreaAttribute();
            case AttributeType.WEIGHT:
                return this.renderNumericAttribute();
            default:
                return this.renderPlaceholder();
        }
    }

    render() {
        const {
            getLink,
            attribute,
            isAvailable,
            attribute: { attribute_code, attribute_value },
            mix,
            isFormattedAsText,
            showProductAttributeAsLink,
            device
        } = this.props;

        if (attribute_code && !attribute_value) {
            return null;
        }

        const href = getLink(attribute);
        // Invert to apply css rule without using not()
        const isNotAvailable = !isAvailable;

        if (isFormattedAsText) {
            return (
                <div className='ProductAttributeValue'>
                    {this.renderAttributeByType()}
                </div>
            );
        }

        if (!showProductAttributeAsLink) {
            if (device?.isMobile) {
                return (
                    <span
                        className={isNotAvailable ? "ProductAttributeValue ProductAttributeValue_isNotAvailable" : 'ProductAttributeValue'}
                        onClick={this.clickHandler}
                        role="link"
                        tabIndex={-1}
                        aria-hidden={isNotAvailable}
                    >
                        {this.renderAttributeByType()}
                    </span>
                );
            }

            return (
                <span
                    className={isNotAvailable ? "ProductAttributeValue ProductAttributeValue_isNotAvailable" : 'ProductAttributeValue'}
                    onClick={this.clickHandler}
                    // onKeyDown={this.onMouseHandler}
                    role="link"
                    tabIndex={-1}
                    aria-hidden={isNotAvailable}
                    onMouseEnter={this.onMouseHandler}
                    onMouseLeave={this.onMouseLeveHandler}
                >
                    {this.renderAttributeByType()}
                </span>
            );
        }

        return (
            <>
                <a
                    className={isNotAvailable ? "ProductAttributeValue ProductAttributeValue_isNotAvailable" : 'ProductAttributeValue'}
                    href={href}
                    onClick={this.clickHandler}
                    aria-hidden={isNotAvailable}
                // onMouseEnter={this.clickHandler}
                >
                    {this.renderAttributeByType()}
                </a>
            </>
        );
    }
}

export default ProductAttributeValueComponent;
